import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getScoreInterpretation } from '../data/fagerstromQuestions';
import { Activity, ArrowRight, Home, RefreshCw, HeartHandshake, Sprout } from 'lucide-react';

export const Results = () => {
  const location = useLocation();
  const { language, t } = useLanguage();
  const result = location.state?.result;

  if (!result) {
    return <Navigate to="/dashboard" replace />;
  }

  const interpretation = getScoreInterpretation(result.total_score, language);
  const isHighDependency = result.total_score >= 6;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="results-page">
      <div className="max-w-3xl mx-auto">
        {/* Result Card */}
        <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-xl animate-scale-in" data-testid="result-card">
          {/* Header with Score */}
          <div className={`${interpretation.bgColor} p-8 sm:p-12 text-center`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full mb-6">
              <Activity className={`w-5 h-5 ${interpretation.textColor}`} />
              <span className={`font-medium ${interpretation.textColor}`}>
                {t('yourResult')}
              </span>
            </div>

            <div className={`text-7xl sm:text-8xl font-extrabold ${interpretation.textColor} mb-4`}>
              {result.total_score}
              <span className="text-3xl font-normal opacity-60">/10</span>
            </div>

            <h2 className={`text-2xl sm:text-3xl font-bold ${interpretation.textColor} mb-4`}>
              {interpretation.level}
            </h2>
          </div>

          {/* Interpretation */}
          <div className="p-8 sm:p-12">
            <div className="flex items-start gap-4 mb-8">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                isHighDependency ? 'bg-orange-100' : 'bg-green-100'
              }`}>
                {isHighDependency ? (
                  <HeartHandshake className={`w-6 h-6 ${isHighDependency ? 'text-orange-600' : 'text-green-600'}`} />
                ) : (
                  <Sprout className="w-6 h-6 text-green-600" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('scoreInterpretation')}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {interpretation.message}
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="rounded-2xl overflow-hidden mb-8">
              <img
                src={isHighDependency 
                  ? "https://images.unsplash.com/photo-1591630060069-7cb1f16261b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxzdXBwb3J0aXZlJTIwY29tZm9ydGluZyUyMGhhbmQlMjBnZXN0dXJlfGVufDB8fHx8MTc3MjM2NDc5M3ww&ixlib=rb-4.1.0&q=85"
                  : "https://images.unsplash.com/photo-1710899829448-a8ae3e55eba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxncm93aW5nJTIwZ3JlZW4lMjBwbGFudCUyMHNwcm91dCUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3MjM2NDc5Mnww&ixlib=rb-4.1.0&q=85"
                }
                alt={isHighDependency ? "Support" : "Growth"}
                className="w-full h-48 object-cover"
              />
            </div>

            {/* Score Breakdown */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">
                {language === 'fr' ? 'Échelle de score' : 'Score scale'}
              </h4>
              <div className="space-y-3">
                {[
                  { range: '0-2', label_fr: 'Très faible', label_en: 'Very low', color: 'bg-green-100 text-green-800' },
                  { range: '3-4', label_fr: 'Faible', label_en: 'Low', color: 'bg-green-200 text-green-900' },
                  { range: '5', label_fr: 'Moyenne', label_en: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
                  { range: '6-7', label_fr: 'Forte', label_en: 'High', color: 'bg-orange-100 text-orange-800' },
                  { range: '8-10', label_fr: 'Très forte', label_en: 'Very high', color: 'bg-red-100 text-red-900' },
                ].map((level, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      level.range.includes(String(result.total_score)) || 
                      (level.range === '0-2' && result.total_score <= 2) ||
                      (level.range === '3-4' && result.total_score >= 3 && result.total_score <= 4) ||
                      (level.range === '6-7' && result.total_score >= 6 && result.total_score <= 7) ||
                      (level.range === '8-10' && result.total_score >= 8)
                        ? level.color + ' font-medium'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <span>{language === 'fr' ? level.label_fr : level.label_en}</span>
                    <span className="font-mono">{level.range}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/dashboard" className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-full border-2"
                  data-testid="back-dashboard-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t('backToDashboard')}
                </Button>
              </Link>
              <Link to="/questionnaire" className="flex-1">
                <Button 
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                  data-testid="retake-test-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('retakeTest')}
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
